import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ui } from "@patient-watch/ui";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useCreateClinicianOrganisationMutation } from "@/features/organisations/api/createClinicianOrganisation";
import { supabaseClient as supabase } from "@/lib/supabase";
import { resolvePinnedClinicianSpecialty } from "@/features/onboarding/utils/clinicianSpecialty";
import { getClinicianPostProfessionalDestination } from "@/features/onboarding/utils/getClinicianPostProfessionalDestination";
import { selectProfileByIdQueryOptions, useProfileQuery } from "@/features/profile/api/selectProfile";
import ProfileWrap from "@/features/profile/layouts/ProfileWrap";
import { useProfileCopy } from "@/features/i18n/profileCopy";
import { Input } from "@patient-watch/forms";
import { useAuth } from "@/utils/auth";

const formSchema = z.object({
    organisation_name: z.string().min(3, "Organisation name must be at least 3 characters"),
});

type FormSchemaType = z.infer<typeof formSchema>;

async function navigateAfterOrganisation(
    navigate: ReturnType<typeof useNavigate>,
    userId: string | undefined,
    pinnedSpecialty: string
) {
    if (pinnedSpecialty && userId) {
        const destination = await getClinicianPostProfessionalDestination(userId);
        void navigate({ to: destination });
        return;
    }
    void navigate({ to: "/onboarding/clinician/professional" });
}

const ClinicianOrganisation = () => {
    const { data: profile } = useProfileQuery();
    const auth = useAuth();
    const navigate = useNavigate();
    const mutation = useCreateClinicianOrganisationMutation();
    const copy = useProfileCopy();
    // Always call hooks before any early return (profile may gain an org after invite apply).
    const form = useForm<FormSchemaType>({
        resolver: zodResolver(formSchema),
    });

    const { data: clinicianRow } = useQuery({
        queryKey: ["clinician-specialty-for-onboarding", auth.id],
        enabled: Boolean(auth.id),
        queryFn: async () => {
            const { data, error } = await supabase
                .from("clinicians")
                .select("specialty")
                .eq("clinician_id", auth.id)
                .maybeSingle();
            if (error) throw error;
            return data;
        },
    });

    const pinnedSpecialty = resolvePinnedClinicianSpecialty({
        clinicianSpecialty: clinicianRow?.specialty,
        userMetaData: profile?.user_meta_data,
    });

    const existingOrganisation = profile?.organisations?.[0];
    const hasExistingOrganisation = Boolean(existingOrganisation?.organisation_name);

    const onSubmit = async (formData: FormSchemaType) => {
        try {
            await mutation.mutateAsync(formData);
            await navigateAfterOrganisation(navigate, auth.id, pinnedSpecialty);
        } catch {
            /* mutation shows toast on error */
        }
    };

    if (hasExistingOrganisation && existingOrganisation?.organisation_name) {
        return (
            <ProfileWrap title={copy.organisation}>
                <ui.Card data-testid="existing-organisation">
                    <ui.CardContent className="pt-6">
                        <div className="space-y-4">
                            <div>
                                <p className="mb-2 text-muted-foreground text-sm">{copy.yourOrganisation}</p>
                                <p className="font-semibold text-lg" data-testid="existing-organisation-name">
                                    {existingOrganisation.organisation_name}
                                </p>
                            </div>
                        </div>
                    </ui.CardContent>
                </ui.Card>

                <ui.Button
                    onClick={() => void navigateAfterOrganisation(navigate, auth.id, pinnedSpecialty)}
                    className="w-full"
                    size="lg"
                    data-testid="continue-button"
                >
                    {copy.continue}
                </ui.Button>
            </ProfileWrap>
        );
    }

    return (
        <ProfileWrap title={copy.organisation} description={copy.organisationDescription}>
            <ui.Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <Input name="organisation_name" placeholder="e.g. London Pain Clinic" autoFocus={true} />

                    <ui.Button
                        type="submit"
                        className="w-full"
                        size="lg"
                        disabled={mutation.isPending}
                        data-testid="continue-button"
                    >
                        {mutation.isPending && <Loader2 className="mr-2 w-4 h-4 animate-spin" />}
                        {mutation.isPending ? copy.creating : copy.continue}
                    </ui.Button>
                </form>
            </ui.Form>
        </ProfileWrap>
    );
};

export const Route = createFileRoute("/onboarding/clinician/organisation")({
    component: ClinicianOrganisation,
    loader: async ({ context }) => {
        if (context?.auth?.id) {
            await context?.queryClient.ensureQueryData(selectProfileByIdQueryOptions(context.auth.id));
        }
    },
});
